import React, { useState, useRef, useEffect } from 'react';
import { Folder, Plus, MoreVertical, Edit2, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function FoldersList({ 
  projects = [],
  chats = [],
  expandedFolders = new Set(),
  onToggleFolder,
  onCreateProject,
  onRenameProject,
  onDeleteProject,
  onSelectFolder,
  onChatSelect,
  currentChatId
}) {
  const [menuOpenProjectId, setMenuOpenProjectId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const createButtonRef = useRef(null);
  const menuRefs = useRef({});
  const buttonRefs = useRef({});

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.values(menuRefs.current).forEach(ref => {
        if (ref && !ref.contains(event.target)) {
          setMenuOpenProjectId(null);
        }
      });
    };

    if (menuOpenProjectId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpenProjectId]);

  // Close menu on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setMenuOpenProjectId(null);
        setEditingProjectId(null);
        setShowCreateModal(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleMenuClick = (e, projectId) => {
    e.stopPropagation();
    if (buttonRefs.current[projectId]) {
      const rect = buttonRefs.current[projectId].getBoundingClientRect();
      setMenuPosition({
        top: rect.top,
        left: rect.right
      });
    }
    setMenuOpenProjectId(menuOpenProjectId === projectId ? null : projectId);
  };

  const handleRename = (project) => {
    setEditingProjectId(project.id);
    setEditingName(project.name || '');
    setMenuOpenProjectId(null);
  };

  const handleRenameSubmit = (projectId) => {
    if (editingName.trim() && onRenameProject) {
      onRenameProject(projectId, editingName.trim());
    }
    setEditingProjectId(null);
    setEditingName('');
  };

  const handleDelete = (projectId) => {
    if (window.confirm('Are you sure you want to delete this folder? All chats in this folder will be moved to no folder.')) {
      if (onDeleteProject) {
        onDeleteProject(projectId);
      }
    }
    setMenuOpenProjectId(null);
  };

  const handleCreateProject = () => {
    if (newProjectName.trim() && onCreateProject) {
      onCreateProject(newProjectName.trim());
      setNewProjectName('');
      setShowCreateModal(false);
    }
  };

  const handleFolderClick = (projectId) => {
    if (onToggleFolder) {
      onToggleFolder(projectId);
    }
    if (onSelectFolder) {
      onSelectFolder(projectId);
    }
  };

  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-1.5 relative">
        <h3 className="text-sm text-gray-600">Folders</h3>
        <div className="relative">
          <button 
            ref={createButtonRef}
            onClick={(e) => {
              if (createButtonRef.current) {
                const rect = createButtonRef.current.getBoundingClientRect();
                setButtonPosition({
                  top: rect.top,
                  left: rect.right
                });
              }
              setShowCreateModal(true);
            }}
            className="p-0.5 rounded hover:bg-gray-100/80 transition-colors text-gray-500 hover:text-gray-700"
            title="New folder"
          >
            <Plus size={14} />
          </button>

          {/* Create Folder Modal - Fixed positioning to escape sidebar, opens to the right */}
          <AnimatePresence>
            {showCreateModal && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[100]"
                  onClick={() => setShowCreateModal(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, x: -10 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.95, x: -10 }}
                  transition={{ duration: 0.15 }}
                  className="fixed w-72 bg-white rounded-lg shadow-2xl border border-gray-200/50 z-[101] p-3"
                  style={{
                    top: `${buttonPosition.top}px`,
                    left: `${buttonPosition.left + 8}px`
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseEnter={(e) => e.stopPropagation()}
                >
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateProject();
                  } else if (e.key === 'Escape') {
                    setShowCreateModal(false);
                    setNewProjectName('');
                  }
                }}
                placeholder="Folder name"
                autoFocus
                className="w-full px-3 py-2 text-sm border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleCreateProject}
                  disabled={!newProjectName.trim()}
                  className="flex-1 px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewProjectName('');
                  }}
                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Folders List */}
      {projects.length === 0 ? (
        <div className="text-xs text-gray-400 pl-0.5">
          No folders yet
        </div>
      ) : (
        <div className="space-y-0.5">
          {projects.map((project) => {
            const isExpanded = expandedFolders.has(project.id);
            const isMenuOpen = menuOpenProjectId === project.id;
            const isEditing = editingProjectId === project.id;

            return (
              <div key={project.id} className="group">
                <div
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-gray-100/60 transition-colors cursor-pointer relative"
                  onClick={() => !isEditing && handleFolderClick(project.id)}
                >
                  {/* Chevron for expand/collapse */}
                  {isExpanded ? (
                    <ChevronDown size={12} className="text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronRight size={12} className="text-gray-400 flex-shrink-0" />
                  )}

                  {/* Folder Icon */}
                  <Folder 
                    size={14} 
                    className="text-gray-400 flex-shrink-0"
                    style={{ color: project.color || '#6B7280' }}
                  />

                  {/* Folder Name */}
                  {isEditing ? (
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={() => handleRenameSubmit(project.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleRenameSubmit(project.id);
                        } else if (e.key === 'Escape') {
                          setEditingProjectId(null);
                          setEditingName('');
                        }
                      }}
                      autoFocus
                      className="flex-1 text-xs text-gray-700 bg-white border border-purple-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-700 truncate group-hover:text-gray-900">
                        {project.name || 'Untitled Folder'}
                      </p>
                    </div>
                  )}

                  {/* Three dots menu - visible on hover, stays open when clicked */}
                  <div className={`transition-opacity ${isMenuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} ref={el => menuRefs.current[project.id] = el}>
                    <button
                      ref={el => buttonRefs.current[project.id] = el}
                      onClick={(e) => handleMenuClick(e, project.id)}
                      className="p-1 rounded hover:bg-gray-200/80 transition-colors text-gray-400"
                      title="More options"
                    >
                      <MoreVertical size={12} />
                    </button>

                    {/* Context Menu - Fixed positioning to escape sidebar, opens to the right */}
                    <AnimatePresence>
                      {isMenuOpen && (
                        <>
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[100]"
                            onClick={() => setMenuOpenProjectId(null)}
                          />
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95, x: -10 }}
                            animate={{ opacity: 1, scale: 1, x: 0 }}
                            exit={{ opacity: 0, scale: 0.95, x: -10 }}
                            transition={{ duration: 0.15 }}
                            className="fixed w-48 bg-white rounded-lg shadow-2xl border border-gray-200/50 z-[101] overflow-hidden"
                            style={{
                              top: `${menuPosition.top}px`,
                              left: `${menuPosition.left + 8}px`
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseEnter={(e) => e.stopPropagation()}
                          >
                            <div className="py-1">
                              <button
                                onClick={() => handleRename(project)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <Edit2 size={14} />
                                <span>Rename project</span>
                              </button>
                              
                              <div className="border-t border-gray-100">
                                <button
                                  onClick={() => handleDelete(project.id)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 size={14} />
                                  <span>Delete project</span>
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Expanded: Show chats in this folder */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-6 pr-2 py-1 space-y-0.5">
                        {chats
                          .filter(chat => chat.folder_id === project.id)
                          .map((chat) => (
                            <div
                              key={chat.id}
                              onClick={() => onChatSelect && onChatSelect(chat.id)}
                              className={`px-2 py-1.5 rounded-lg text-xs cursor-pointer transition-colors ${
                                currentChatId === chat.id
                                  ? 'bg-purple-100/60 text-purple-900 font-medium'
                                  : 'text-gray-700 hover:bg-gray-100/60'
                              }`}
                            >
                              <p className="truncate">{chat.title || 'Untitled Chat'}</p>
                            </div>
                          ))}
                        {chats.filter(chat => chat.folder_id === project.id).length === 0 && (
                          <div className="px-2 py-1 text-xs text-gray-400 italic">
                            No chats in this folder
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
